import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Check, X } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../i18n/LanguageContext";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 6) {
        setState("pending");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.authorized || data.captured) {
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div data-testid="payment-return-page" className="flex min-h-screen items-center justify-center bg-white px-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-lg text-center"
      >
        {state === "checking" && (
          <>
            <Loader2 size={40} className="mx-auto animate-spin text-[#050505]" />
            <p className="mt-8 text-lg font-light text-[#4a4a4a]">{t("payment.checking")}</p>
          </>
        )}

        {state === "success" && (
          <>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#050505]">
              <Check size={30} className="text-white" strokeWidth={2.5} />
            </div>
            <h1 className="font-display mt-10 text-4xl font-light tracking-tightest text-[#050505] md:text-5xl">
              {t("payment.successTitle")}
            </h1>
            <p className="mt-5 text-lg font-light text-[#4a4a4a]">{t("payment.successBody")}</p>
            <button
              data-testid="payment-home-btn"
              onClick={() => navigate("/")}
              className="ep-btn ep-btn-dark mt-10"
            >
              {t("payment.home")}
            </button>
          </>
        )}

        {state === "pending" && (
          <>
            <Loader2 size={40} className="mx-auto animate-spin text-[#050505]" />
            <h1 className="font-display mt-8 text-3xl font-light tracking-tightest text-[#050505]">
              {t("payment.pendingTitle")}
            </h1>
            <p className="mt-4 text-base font-light text-[#4a4a4a]">{t("payment.pendingBody")}</p>
            <button onClick={() => navigate("/")} className="ep-btn ep-btn-ghost mt-8">
              {t("payment.home")}
            </button>
          </>
        )}

        {state === "error" && (
          <>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-black/8 bg-[#fafafa]">
              <X size={28} className="text-[#050505]" />
            </div>
            <h1 className="font-display mt-8 text-3xl font-light tracking-tightest text-[#050505]">
              {t("payment.errorTitle")}
            </h1>
            <p className="mt-4 text-base font-light text-[#4a4a4a]">{t("payment.errorBody")}</p>
            <button
              data-testid="payment-retry-btn"
              onClick={() => navigate("/reservar")}
              className="ep-btn ep-btn-dark mt-8"
            >
              {t("payment.retry")}
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
